"use client";

import { useState, useEffect } from "react";
import { useAppSelector } from "@/hooks/hooks";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { ChevronRight, ChevronLeft } from "lucide-react";
import Slider from "react-slick";
import "../styles/carousel.css";

// Slider Arrows matching home page testimonials
function NextArrow(props: any) {
  const { onClick } = props;
  return (
    <button
      onClick={onClick}
      className="hidden sm:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-lg items-center justify-center hover:bg-pink-600 hover:text-white transition cursor-pointer border border-gray-100"
      aria-label="Next slide"
    >
      <ChevronRight className="w-5 h-5" />
    </button>
  );
}

function PrevArrow(props: any) {
  const { onClick } = props;
  return (
    <button
      onClick={onClick}
      className="hidden sm:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-lg items-center justify-center hover:bg-pink-600 hover:text-white transition cursor-pointer border border-gray-100"
      aria-label="Previous slide"
    >
      <ChevronLeft className="w-5 h-5" />
    </button>
  );
}

export default function Experiences({ cmsData }: { cmsData?: any }) {
  const { homeDetails } = useAppSelector((state) => state.home);
  const experienceList = homeDetails?.ExperienceList ?? [];

  // Compute slidesToShow from actual window width (fixes SSR + mobile issue)
  const getSlidesToShow = () => {
    if (typeof window === "undefined") return 4;
    const w = window.innerWidth;
    if (w <= 640) return 1;
    if (w <= 768) return 2;
    if (w <= 1024) return 3;
    return 4;
  };

  const [slidesCount, setSlidesCount] = useState(getSlidesToShow);

  useEffect(() => {
    const onResize = () => setSlidesCount(getSlidesToShow());
    window.addEventListener("resize", onResize);
    onResize(); // apply immediately on mount
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const displayExperiences =
    experienceList.length > 0
      ? experienceList.map((exp: any) => {
          return {
            title: exp.Title || "",
            description: exp.ResortName || "",
            image:
              exp.ImageUpload ||
              "https://images.unsplash.com/photo-1706013706284-cec9fe6f55e9?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=600",
          };
        })
      : [];

  if (displayExperiences.length === 0) {
    return null;
  }

  const showArrows = slidesCount > 1 && displayExperiences.length > 1;

  const sliderSettings = {
    dots: true,
    infinite: displayExperiences.length > slidesCount,
    speed: 500,
    slidesToShow: slidesCount,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    swipe: true,
    touchMove: true,
    arrows: showArrows,
    nextArrow: showArrows ? <NextArrow /> : undefined,
    prevArrow: showArrows ? <PrevArrow /> : undefined,
  };

  return (
    <section id="experiences" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        {cmsData?.Template ? (
          <div
            dangerouslySetInnerHTML={{ __html: cmsData.Template }}
            className="text-center mb-12"
          />
        ) : (
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl mb-4 font-sans font-semibold text-gray-900">
              Experience The{" "}
              <span className="bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 bg-clip-text text-transparent">
                Magic
              </span>
            </h2>
            <p className="text-xl text-gray-600 font-sans font-normal">
              Unforgettable Moments Await You
            </p>
          </div>
        )}

        {displayExperiences.length > 1 ? (
          <div className="relative select-text overflow-visible">
            <Slider {...sliderSettings}>
              {displayExperiences.map((exp: any, index: number) => (
                <div key={index} className="px-3 pb-4">
                  <div className="group relative overflow-hidden rounded-2xl shadow-lg cursor-pointer h-80 flex flex-col justify-end">
                    {/* Full height image background */}
                    <div className="absolute inset-0">
                      <ImageWithFallback
                        src={exp.image}
                        alt={exp.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {/* Extremely subtle dark gradient fade at the very bottom for text readability */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent z-0"></div>
                    </div>

                    {/* Content overlaying the image at the bottom */}
                    <div className="relative p-6 text-white z-10">
                      <h3 className="text-xl font-sans font-medium mb-1 drop-shadow-[0_2px_3px_rgba(0,0,0,0.7)]">
                        {exp.title}
                      </h3>
                      {exp.description && (
                        <p className="text-sm text-gray-200 font-sans font-normal mt-1 leading-relaxed drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]">
                          {exp.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 select-text">
            {displayExperiences.map((exp: any, index: number) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-2xl shadow-lg cursor-pointer h-80 flex flex-col justify-end"
              >
                {/* Full height image background */}
                <div className="absolute inset-0">
                  <ImageWithFallback
                    src={exp.image}
                    alt={exp.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {/* Extremely subtle dark gradient fade at the very bottom for text readability */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent z-0"></div>
                </div>

                {/* Content overlaying the image at the bottom */}
                <div className="relative p-6 text-white z-10">
                  <h3 className="text-xl font-sans font-medium mb-1 drop-shadow-[0_2px_3px_rgba(0,0,0,0.7)]">
                    {exp.title}
                  </h3>
                  {exp.description && (
                    <p className="text-sm text-gray-200 font-sans font-normal mt-1 leading-relaxed drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]">
                      {exp.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
